import { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Send, X, Maximize2, Minimize2, Plus, ExternalLink, MessageSquarePlus, Mic, MicOff, Save } from 'lucide-react'
import { useBranding } from '../../hooks/useBranding'
import { useHortonChat, type ChatMessage } from './HortonChatContext'
import { DreamHierarchyDisplay, type ItemState, type RejectedItem } from './DreamHierarchyDisplay'
import { FeedbackModal } from '../feedback/FeedbackModal'
import { useVoiceInput } from '../../hooks/useVoiceInput'
import { BRAND } from '../../config/brand'

const STORAGE_KEY = 'horton_fab_pos'
const HORTON_SIZE = 46
const FEEDBACK_SIZE = 38
const GAP = 6
const PILL_W = HORTON_SIZE
const PILL_H = HORTON_SIZE + GAP + FEEDBACK_SIZE
const MARGIN = 8

function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(max, val))
}

function loadSavedPosition(): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return null
}

function savePosition(x: number, y: number) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ x, y }))
  } catch { /* ignore */ }
}

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.type === 'user'
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? 'bg-brand text-on-brand rounded-br-md'
            : 'bg-surface-alt text-primary rounded-bl-md'
        }`}
      >
        {msg.content || (
          <span className="inline-flex gap-1 text-muted">
            <span className="animate-bounce">.</span>
            <span className="animate-bounce [animation-delay:0.15s]">.</span>
            <span className="animate-bounce [animation-delay:0.3s]">.</span>
          </span>
        )}
      </div>
    </div>
  )
}

export function FloatingHortonButton() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const branding = useBranding()
  const {
    messages,
    isLoading,
    chatId,
    v3ComposedResponse,
    hasItems,
    sendMessage,
    newChat,
    persona,
    setPersona,
  } = useHortonChat()

  const [chatOpen, setChatOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [input, setInput] = useState('')
  const [itemStates, setItemStates] = useState<Map<string, ItemState>>(new Map())
  const [rejectedItems, setRejectedItems] = useState<RejectedItem[]>([])

  const prevItemCount = useRef(0)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Drag state -- applied to the whole pill
  const pillRef = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)
  const dragMoved = useRef(false)
  const startTouch = useRef({ x: 0, y: 0 })
  const startPos = useRef({ x: 0, y: 0 })
  const tapTarget = useRef<'horton' | 'feedback' | null>(null)

  const defaultPos = useCallback(() => {
    const vw = window.innerWidth
    const vh = window.innerHeight
    return { x: vw - PILL_W - 16, y: vh - PILL_H - 96 }
  }, [])

  const [pos, setPos] = useState<{ x: number; y: number }>(() => {
    return loadSavedPosition() || defaultPos()
  })

  useEffect(() => {
    const saved = loadSavedPosition()
    if (saved) {
      setPos({
        x: clamp(saved.x, MARGIN, window.innerWidth - PILL_W - MARGIN),
        y: clamp(saved.y, MARGIN, window.innerHeight - PILL_H - MARGIN),
      })
    }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      setPos(prev => ({
        x: clamp(prev.x, MARGIN, window.innerWidth - PILL_W - MARGIN),
        y: clamp(prev.y, MARGIN, window.innerHeight - PILL_H - MARGIN),
      }))
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  useEffect(() => {
    if (chatOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [chatOpen])

  useEffect(() => {
    const currentCount = v3ComposedResponse?.items_summary?.length ?? 0
    if (currentCount > 0 && currentCount > prevItemCount.current && chatOpen) {
      setIsExpanded(true)
    }
    prevItemCount.current = currentCount
  }, [v3ComposedResponse?.items_summary?.length, chatOpen])

  const voice = useVoiceInput({
    onInterim: (text) => setInput(text),
    onResult: (text) => setInput(prev => prev ? prev : text),
  })

  const isHortonPage = pathname === '/horton-ai' || pathname === '/horton'

  const handlePointerDown = useCallback((e: React.PointerEvent, target: 'horton' | 'feedback') => {
    dragging.current = true
    dragMoved.current = false
    tapTarget.current = target
    startTouch.current = { x: e.clientX, y: e.clientY }
    startPos.current = { ...pos }
    pillRef.current?.setPointerCapture(e.pointerId)
    e.preventDefault()
  }, [pos])

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return
    const dx = e.clientX - startTouch.current.x
    const dy = e.clientY - startTouch.current.y
    if (!dragMoved.current && Math.abs(dx) < 4 && Math.abs(dy) < 4) return
    dragMoved.current = true
    setPos({
      x: clamp(startPos.current.x + dx, MARGIN, window.innerWidth - PILL_W - MARGIN),
      y: clamp(startPos.current.y + dy, MARGIN, window.innerHeight - PILL_H - MARGIN),
    })
  }, [])

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return
    dragging.current = false
    pillRef.current?.releasePointerCapture(e.pointerId)

    if (dragMoved.current) {
      const vw = window.innerWidth
      const finalX = clamp(
        startPos.current.x + (e.clientX - startTouch.current.x),
        MARGIN,
        vw - PILL_W - MARGIN,
      )
      const finalY = clamp(
        startPos.current.y + (e.clientY - startTouch.current.y),
        MARGIN,
        window.innerHeight - PILL_H - MARGIN,
      )
      const snapX = finalX < vw / 2 ? MARGIN : vw - PILL_W - MARGIN
      setPos({ x: snapX, y: finalY })
      savePosition(snapX, finalY)
    } else {
      if (tapTarget.current === 'horton' && !isHortonPage) {
        setChatOpen(true)
      } else if (tapTarget.current === 'feedback') {
        setFeedbackOpen(true)
      }
    }
    tapTarget.current = null
  }, [isHortonPage])

  const handleSend = async () => {
    const text = input.trim()
    if (!text || isLoading) return
    setInput('')
    await sendMessage(text)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleOpenFullPage = () => {
    // Hand off the floating chat to the full page so it loads the same conversation
    sessionStorage.setItem('horton_current_chat_id', chatId)
    setChatOpen(false)
    navigate('/horton-ai')
  }

  const handleItemStateChange = (itemKey: string, state: ItemState) => {
    setItemStates(prev => new Map(prev).set(itemKey, state))
  }

  const handleRejectedItemsChange = (items: RejectedItem[]) => {
    setRejectedItems(items)
  }

  const anyModalOpen = chatOpen || feedbackOpen

  return (
    <>
      {/* Unified FAB pill */}
      {!anyModalOpen && (
        <div
          ref={pillRef}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          className="fixed z-50 flex flex-col items-center select-none touch-none"
          style={{
            left: pos.x,
            top: pos.y,
            transition: dragging.current ? 'none' : 'left 0.25s ease, top 0.05s ease',
          }}
        >
          {/* the assistant icon */}
          {!isHortonPage && (
            <button
              onPointerDown={e => handlePointerDown(e, 'horton')}
              aria-label={`Talk to ${BRAND.assistant}`}
              className="relative rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-shadow active:scale-95 ring-2 ring-brand/20"
              style={{ width: HORTON_SIZE, height: HORTON_SIZE }}
            >
              <img
                src={branding.icon}
                alt={`${BRAND.assistant}`}
                className="w-full h-full rounded-full pointer-events-none"
              />
              {messages.length > 0 && (
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-brand rounded-full border-2 border-surface" />
              )}
            </button>
          )}

          {/* Feedback icon */}
          <button
            onPointerDown={e => handlePointerDown(e, 'feedback')}
            aria-label="Send feedback"
            className="rounded-full bg-brand hover:bg-brand-hover text-on-brand shadow-lg hover:shadow-xl flex items-center justify-center transition-shadow active:scale-95"
            style={{
              width: FEEDBACK_SIZE,
              height: FEEDBACK_SIZE,
              marginTop: isHortonPage ? 0 : GAP,
            }}
          >
            <MessageSquarePlus className="w-4 h-4 pointer-events-none" />
          </button>
        </div>
      )}

      {/* Feedback modal */}
      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      {/* the assistant chat modal */}
      {chatOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end p-0 sm:p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setChatOpen(false)}
          />

          <div
            className={`relative flex bg-surface border border-soft rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
              isExpanded
                ? 'w-full sm:w-[min(900px,95vw)] h-[90dvh] sm:h-[80vh] flex-row'
                : 'w-full sm:w-[400px] h-[70dvh] sm:h-[520px] flex-col'
            }`}
          >
            {/* Chat column */}
            <div className={`flex flex-col min-w-0 ${isExpanded ? 'w-full sm:w-[380px] border-r border-soft' : 'flex-1'}`}>
              {/* Header */}
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-soft bg-surface flex-shrink-0">
                <img src={branding.icon} alt={`${BRAND.assistant}`} className="w-7 h-7 rounded-lg flex-shrink-0" />
                <span className="font-semibold text-primary text-sm flex-1 truncate">{BRAND.assistant}</span>

                <select
                  value={persona}
                  onChange={e => setPersona(e.target.value)}
                  className="text-xs border border-soft rounded-lg px-1.5 py-1 bg-surface text-secondary max-w-[90px]"
                  aria-label="Coaching style"
                >
                  <option value="supportive_best_friend">Friend</option>
                  <option value="calm_older_sibling">Sibling</option>
                  <option value="quiet_mentor">Mentor</option>
                </select>

                {messages.length > 0 && (
                  <button
                    onClick={newChat}
                    className="p-1.5 rounded-lg hover:bg-surface-alt text-muted hover:text-secondary transition-colors"
                    title="New chat"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                )}

                <button
                  onClick={() => setIsExpanded(prev => !prev)}
                  className="p-1.5 rounded-lg hover:bg-surface-alt text-muted hover:text-secondary transition-colors hidden sm:flex"
                  title={isExpanded ? 'Minimize' : 'Expand'}
                >
                  {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>

                <button
                  onClick={handleOpenFullPage}
                  className="p-1.5 rounded-lg hover:bg-surface-alt text-muted hover:text-secondary transition-colors"
                  title="Open full page"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>

                <button
                  onClick={() => setChatOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-surface-alt text-muted hover:text-secondary transition-colors"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    <img src={branding.icon} alt="" className="w-14 h-14 mb-3 opacity-80" />
                    <p className="text-sm font-medium text-primary mb-1">Chat with {BRAND.assistant}</p>
                    <p className="text-xs text-muted leading-relaxed">
                      Share your thoughts and I'll help turn them into dreams, goals, and actionable plans.
                    </p>
                  </div>
                ) : (
                  messages.map(msg => <MessageBubble key={msg.id} msg={msg} />)
                )}
              </div>

              {/* Items found banner */}
              {hasItems && (
                <div className="border-t border-soft px-3 py-2 flex-shrink-0 bg-brand-subtle">
                  <button
                    onClick={handleOpenFullPage}
                    className="w-full flex items-center justify-between gap-2 text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-base">🌱</span>
                      <span className="text-xs font-medium text-brand truncate">
                        {v3ComposedResponse?.items_summary?.length || 0} item(s) ready
                      </span>
                    </div>
                    <span className="flex items-center gap-1 text-xs font-semibold text-brand whitespace-nowrap">
                      <Save className="w-3.5 h-3.5" />
                      Save to Life Map
                    </span>
                  </button>
                </div>
              )}

              {/* Input */}
              <div className="border-t border-soft p-3 flex-shrink-0">
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={voice.isListening ? 'Listening…' : 'Type a message...'}
                    rows={1}
                    className={`flex-1 resize-none rounded-xl border bg-surface-alt px-3 py-2 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/40 min-h-10 max-h-[100px] ${voice.isListening ? 'border-brand ring-2 ring-brand/30' : 'border-soft'}`}
                    style={{ fieldSizing: 'content' } as React.CSSProperties}
                  />
                  {voice.isSupported && (
                    <button
                      onClick={voice.toggle}
                      className={`p-2.5 rounded-xl transition-colors flex-shrink-0 ${
                        voice.isListening
                          ? 'bg-bad/15 text-bad hover:bg-bad/25 animate-pulse'
                          : 'bg-surface-alt text-muted hover:text-secondary hover:bg-surface-alt/80'
                      }`}
                      aria-label={voice.isListening ? 'Stop listening' : 'Voice input'}
                      title={voice.isListening ? 'Stop listening' : 'Voice input'}
                    >
                      {voice.isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </button>
                  )}
                  <button
                    onClick={handleSend}
                    disabled={isLoading || !input.trim()}
                    className="p-2.5 rounded-xl bg-brand text-on-brand hover:bg-brand-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    aria-label="Send"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Items pane (expanded only, desktop) */}
            {isExpanded && (
              <div className="hidden sm:flex flex-col flex-1 min-w-0 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-soft bg-surface flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-primary">Your Items</h3>
                      <p className="text-xs text-muted">
                        {v3ComposedResponse?.items_summary?.length || 0} items from this conversation
                      </p>
                    </div>
                    {hasItems && (
                      <button
                        onClick={handleOpenFullPage}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gradient-to-r from-brand to-info text-on-brand hover:brightness-110 transition-all shadow-sm"
                      >
                        <Save className="w-3.5 h-3.5" />
                        Save to Life Map
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 min-h-0">
                  {hasItems ? (
                    <DreamHierarchyDisplay
                      composedResponse={v3ComposedResponse || undefined}
                      itemStates={itemStates}
                      onItemStateChange={handleItemStateChange}
                      rejectedItems={rejectedItems}
                      onRejectedItemsChange={handleRejectedItemsChange}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center text-muted">
                      <div className="text-4xl mb-3 opacity-50">🌱</div>
                      <p className="text-sm">No items yet.</p>
                      <p className="text-xs mt-1">Keep chatting and items will appear here.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
